import { useState } from "react";
import {
  useRealtimeKitMeeting,
  useRealtimeKitSelector,
} from "@cloudflare/realtimekit-react";
import { Users } from "lucide-react";
import { MeetingLogoutButton } from "./MeetingLogoutButton";
import { LivestreamHostSidebar } from "./LivestreamHostSidebar";
import { LivestreamVideoGrid } from "./LivestreamVideoGrid";
import { LivestreamRandomParticipantOverlay } from "./LivestreamRandomParticipantOverlay";
import { LivestreamPinnedCommentOverlay } from "./LivestreamPinnedCommentOverlay";
import { LivestreamViewerFrameOverlay } from "./LivestreamViewerFrameOverlay";
import { useLivestreamChat } from "./useLivestreamChat";
import { useLivestreamPinnedComment } from "./useLivestreamPinnedComment";
import { useLivestreamRandomParticipant } from "./useLivestreamRandomParticipant";
import { useLivestreamStageFeeds } from "./useLivestreamStageFeeds";
import { useLivestreamRequestJoinVisibility } from "./useLivestreamRequestJoinVisibility";
import { useLivestreamStageHost } from "./useLivestreamStageHost";

export function LivestreamAdminView({
  shopProductIds,
  showLogout,
  loggingOut,
  onLogout,
}: {
  shopProductIds: string[];
  showLogout: boolean;
  loggingOut: boolean;
  onLogout: () => void;
}) {
  const { meeting } = useRealtimeKitMeeting();
  const selfId = useRealtimeKitSelector((m) => m.self.id);
  const roomState = useRealtimeKitSelector((m) => m.self.roomState);
  const canKickParticipants = useRealtimeKitSelector(
    (m) => m.self.permissions.kickParticipant,
  );
  const showLogoutButton = showLogout && roomState !== "joined";
  const { messages, sendMessage } = useLivestreamChat({ meeting });
  const { pinnedComment, togglePinComment } = useLivestreamPinnedComment({
    meeting,
    isHost: true,
    messages,
  });
  const hasShopProducts = shopProductIds.length > 0;
  const {
    requestJoinVisible,
    setRequestJoinVisible,
    shopVisible,
    setShopVisible,
  } = useLivestreamRequestJoinVisibility({
    meeting,
    isHost: true,
  });
  const stageFeeds = useLivestreamStageFeeds({ meeting, includeSelf: false }).filter(
    (participant) => participant.id !== selfId,
  );
  const {
    participants,
    stageRequests,
    grantAccess,
    denyAccess,
    removeFromStage,
    kickFromMeeting,
    actingOnUserId,
  } = useLivestreamStageHost({ meeting });
  const { activeDraw, clearActiveDraw, selectRandomParticipant } =
    useLivestreamRandomParticipant({ meeting });
  const [selectingRandomParticipant, setSelectingRandomParticipant] = useState(false);
  const eligibleRandomParticipantNames = participants
    .filter((participant) => !participant.isSelf)
    .map((participant) => participant.name);
  const hasStageScreenShare = stageFeeds.some(
    (participant) =>
      participant.screenShareEnabled && Boolean(participant.screenShareTracks?.video),
  );
  const showViewerFrameOverlay =
    stageFeeds.length === 1 && !hasStageScreenShare;
  const participantCount = participants.length;

  if (!meeting) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-black text-white">
        Connecting...
      </div>
    );
  }

  return (
    <div className="flex h-dvh w-screen bg-black">
      <div className="@container relative min-w-0 flex-1 [container-type:size]">
        {showLogoutButton ? (
          <MeetingLogoutButton
            showLogout={showLogoutButton}
            loggingOut={loggingOut}
            onLogout={onLogout}
          />
        ) : null}
        {participantCount > 0 ? (
          <div
            className="pointer-events-none absolute top-[max(0.75rem,env(safe-area-inset-top))] left-3 z-20 flex items-center gap-1.5 rounded-full border border-white/20 bg-black/55 px-2.5 py-1 text-sm text-white backdrop-blur-sm sm:left-4"
            aria-label={`${participantCount} participant${participantCount === 1 ? "" : "s"}`}
          >
            <Users className="h-3.5 w-3.5" aria-hidden />
            <span>{participantCount}</span>
          </div>
        ) : null}
        <LivestreamVideoGrid participants={stageFeeds} selfId={selfId} />
        <LivestreamRandomParticipantOverlay draw={activeDraw} onComplete={clearActiveDraw} />
        <LivestreamPinnedCommentOverlay pinnedComment={pinnedComment} />
        {showViewerFrameOverlay ? <LivestreamViewerFrameOverlay /> : null}
      </div>
      <LivestreamHostSidebar
        isAdmin
        messages={messages}
        onSendMessage={sendMessage}
        pinnedComment={pinnedComment}
        onTogglePinComment={togglePinComment}
        participants={participants}
        stageRequests={stageRequests}
        onGrantStageAccess={async ({ userId }) => {
          if (hasStageScreenShare) return;
          await grantAccess({ userId });
        }}
        onDenyStageAccess={denyAccess}
        onRemoveFromStage={removeFromStage}
        onKickFromMeeting={kickFromMeeting}
        onSelectRandomParticipant={async () => {
          if (selectingRandomParticipant || eligibleRandomParticipantNames.length === 0) return;
          setSelectingRandomParticipant(true);
          try {
            await selectRandomParticipant({
              eligibleNames: eligibleRandomParticipantNames,
            });
          } finally {
            setSelectingRandomParticipant(false);
          }
        }}
        selectingRandomParticipant={selectingRandomParticipant}
        canSelectRandomParticipant={eligibleRandomParticipantNames.length > 0}
        actingOnUserId={actingOnUserId}
        canGrantStageAccess={!hasStageScreenShare}
        canKickParticipants={canKickParticipants}
        isLive={false}
        goingLive={false}
        endingStream={false}
        onGoLive={() => {}}
        onEndStream={() => {}}
        audioEnabled={false}
        videoEnabled={false}
        screenShareEnabled={false}
        canShareScreen={false}
        canRecord={false}
        isRecordingActive={false}
        recordingBusy={false}
        onToggleRecording={() => {}}
        onToggleAudio={() => {}}
        onToggleVideo={() => {}}
        onToggleScreenShare={() => {}}
        requestJoinVisible={requestJoinVisible}
        onRequestJoinVisibleChange={({ visible }) => {
          void setRequestJoinVisible({ visible });
        }}
        hasShopProducts={hasShopProducts}
        shopVisible={shopVisible}
        onShopVisibleChange={({ visible }) => {
          void setShopVisible({ visible });
        }}
      />
    </div>
  );
}
